import React from 'react';
import { connect } from 'react-redux';
import { AnyAction, bindActionCreators, Dispatch } from 'redux';

import withRouterPath, {
    InjectedProps as WithRouterPathProps,
} from '../../utils/hocs/with-router-path/with-router-path';
import withUatraits, { InjectedProps as WithUatraitsProps } from '../../utils/hocs/with-uatraits';
import HintComponent, { IncludedData } from '../hint/hint';
import * as userSelectors from '../user/selectors';
import { StoreWithUserState } from '../user/selectors';

import * as wizardActions from './actions';
import * as wizardSelectors from './selectors';
import { Hint, HINT_KIND, HintInnerData, HintData } from './typings';

function getElementByXpath(path: string): Node | null {
    return document.evaluate(path, document.body, null, XPathResult.FIRST_ORDERED_NODE_TYPE, null).singleNodeValue;
}

const RETRY_DELAY_MS = 1000;
const initialHint: Hint = {
    id: '',
    kind: HINT_KIND.CALL_TO_ACTION,
    xpath: '',
};

interface StoreProps {
    getHintViewedCount: ReturnType<typeof userSelectors.getHintViewedCount>;
    hints: Array<Hint>;
    loading: boolean;
    validHints: Array<Hint>;
    variables: ReturnType<typeof userSelectors.getWizardVariables>;
}

interface DispatchProps {
    setConfig: typeof wizardActions.setConfig;
}

interface OwnProps extends WithRouterPathProps, WithUatraitsProps {
    setWizardHints: (hintData: HintData) => void;
}

interface Props extends OwnProps, DispatchProps, StoreProps {}

interface State {
    target: Node | null;
    hint: Hint;
    viewedCount: number;
    includedData: IncludedData;
}

class Wizard extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);

        this.state = {
            target: null,
            hint: initialHint,
            viewedCount: 0,
            includedData: {},
        };
    }

    componentDidMount() {
        this.init();
        this.findTarget();
    }

    componentDidUpdate({ hints: prevHints, location: { pathname: prevPathname } }: Props) {
        const {
            hints,
            location: { pathname },
        } = this.props;
        const {
            hint: { id },
        } = this.state;

        if (pathname !== prevPathname && id) {
            this.closeHint();
            clearTimeout(this.timer);
            this.timer = setTimeout(this.findTarget, 0);

            return;
        }

        if (!id && prevHints !== hints) {
            // хинта еще нет и прилетели новые пропы
            clearTimeout(this.timer);
            this.timer = setTimeout(this.findTarget, 0);

            return;
        }

        if (hints.find((hint) => hint && hint.id === id)) {
            return; // текущий хинт остался прежним и не менялся роут
        }

        if (id) {
            // если прошлый хинт больше не подходит под условия то закрываем его обновив данные о нем
            this.updateHint();
            this.closeHint();
        }
    }

    componentWillUnmount() {
        clearTimeout(this.timer);
    }

    timer: ReturnType<typeof setTimeout>;

    init = () => {
        const { setConfig } = this.props;

        // @ts-ignore
        setConfig(window._data?.hints);
    };

    updateHint = () => {
        const { setWizardHints } = this.props;
        const {
            hint: { id, countViews },
            viewedCount,
            includedData,
        } = this.state;

        const hintInnerData: HintInnerData = {
            viewedDate: new Date().toISOString(),
        };

        if (countViews) {
            hintInnerData.viewedCount = viewedCount + 1;
        }

        setWizardHints({ [id]: { ...hintInnerData, ...includedData } });
    };

    closeHint = () => {
        this.setState({ hint: initialHint, target: null, viewedCount: 0, includedData: {} });
    };

    findTarget = () => {
        const { validHints, getHintViewedCount, variables } = this.props;

        if (!validHints || validHints.length === 0) {
            return;
        }

        for (const hint of validHints) {
            const { xpath, id, includedVariables = {} } = hint;
            const target = getElementByXpath(xpath);
            const viewedCount = getHintViewedCount(id) || 0;

            if (target) {
                const includedData: IncludedData = {};
                for (const key of Object.keys(includedVariables)) {
                    includedData[key] = variables[includedVariables[key]];
                }

                this.setState({ target, hint, viewedCount, includedData });

                return;
            }
        }

        clearTimeout(this.timer);

        // страница могла еще не загрузить ни один элемент, поэтому делаем ретрай
        this.timer = setTimeout(this.findTarget, RETRY_DELAY_MS);
    };

    render() {
        const {
            loading,
            uatraits: { isMobile },
            setWizardHints,
        } = this.props;
        const { hint, target, viewedCount, includedData } = this.state;
        const { id, kind: type, gravity: direction, offset = {}, ...hintProps } = hint;
        const { top: offsetTop, left: offsetLeft } = offset;

        if (isMobile) {
            return null;
        }

        if (!hint || !target || loading) {
            return null;
        }

        return (
            <HintComponent
                direction={direction}
                id={id}
                includedData={includedData}
                offsetLeft={offsetLeft}
                offsetTop={offsetTop}
                onClose={this.closeHint}
                setWizardHints={setWizardHints}
                targetRef={target}
                type={type}
                viewedCount={viewedCount}
                {...hintProps}
            />
        );
    }
}

const mapStateToProps = (storeState: StoreWithUserState): StoreProps => ({
    hints: wizardSelectors.getMatchedHints(storeState),
    loading: wizardSelectors.getIsLoading(storeState),
    validHints: wizardSelectors.getValidHints(storeState),
    getHintViewedCount: userSelectors.getHintViewedCount(storeState),
    variables: userSelectors.getWizardVariables(storeState),
});

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>): DispatchProps => {
    const { setConfig } = wizardActions;

    return bindActionCreators({ setConfig }, dispatch);
};

// @ts-ignore
export default withUatraits(withRouterPath(connect(mapStateToProps, mapDispatchToProps)(Wizard)));
